export type LessonScope = 'PAST' | 'FUTURE'
export interface LessonPerson {
  id: string
  name: string | null
  phone: string | null
  email: string | null
}
export interface LessonNetwork {
  role: string
  samples: number
  status: 'NORMAL' | 'UNSTABLE' | 'UNKNOWN'
  maxRttMs: number | null
  maxPacketLossRatio: number | null
  worstQuality: number | null
  reconnectSamples: number
  lastSampleAt: number | null
}
export interface Lesson {
  id: string
  teacher: LessonPerson
  student: LessonPerson
  classStartTime: number
  status: string
  bookingStatus: string
  type: string
  classroomStatus: string
  absentType: string | null
  absentReason: string | null
  classRecordId: string | null
  classStartedAt: number | null
  classEndedAt: number | null
  teacherJoinedAt: number | null
  studentJoinedAt: number | null
  teacherSeconds: number | null
  studentSeconds: number | null
  teacherLateSeconds: number | null
  studentLateSeconds: number | null
  teacherLate: boolean | null
  studentLate: boolean | null
  summaryStatus: string
  summaryDueAt: number | null
  summarySubmittedAt: number | null
  desiredExpiresAt: number | null
  requestedByAdminUserId: string | null
  cancelReason: string | null
  cancelTime: number | null
  remark: string
  network: LessonNetwork[]
}
export interface LessonPage {
  content: Lesson[]
  total: number
  counts: Record<string, number>
  asOf: number
}
export interface LessonDetail {
  lesson: Lesson
  studentEvaluation: string | null
  words: string[]
  sentences: string[]
  grammarPoints: string | null
  homework: string | null
  nextClassPlan: string | null
  recordings: string[]
  networkRetentionDays: number
}
export interface LessonQuery {
  scope: LessonScope
  from: number
  to: number
  teacher: string
  student: string
  status: string
  focus: string
  page: number
  size: number
}

/** Whose cancellation an admin records: only TEACHER counts against the teacher. */
export type CancelOnBehalfOf = 'STUDENT' | 'TEACHER' | 'ADMIN'
export interface CancelAppointmentReq {
  reason: string
  onBehalfOf: CancelOnBehalfOf
}
export const onBehalfOptions: { value: CancelOnBehalfOf; label: string }[] = [
  { value: 'ADMIN', label: '管理员取消（不计入老师）' },
  { value: 'STUDENT', label: '代学生取消（不计入老师）' },
  { value: 'TEACHER', label: '代老师取消（计入老师取消）' },
]

/** A lesson the system settled as both absent can still become a cancellation. */
export function bothAbsent(lesson: Lesson): boolean {
  return lesson.status === 'ABSENT' && lesson.absentType === 'BOTH'
}

/** Past lessons are settled; only a both-absent one can still be cancelled. */
export function canCancel(lesson: Lesson, past: boolean): boolean {
  return past ? bothAbsent(lesson) : true
}

/** Cancelling for a student or teacher needs their reason. */
export function cancelReasonMissing(req: CancelAppointmentReq): boolean {
  return req.onBehalfOf !== 'ADMIN' && !req.reason.trim()
}

export const bookingLabels: Record<string, string> = {
  CREATED: '已预约 · 待老师确认',
  UNCONFIRMED: '超时未确认',
  AWAITING_ACCEPTANCE: '期望申请 · 待老师接受',
  CONFIRMED: '已确认',
  CANCELLED: '已取消 / 已拒绝',
  EXPIRED: '期望申请已过期',
  COMPLETED: '已完成',
  WAIT_SUMMARY: '待总结',
  ABSENT: '缺席',
}
export const classroomLabels: Record<string, string> = {
  UPCOMING: '尚未开始',
  IN_PROGRESS: '进行中',
  ENDED: '已结束',
  NOT_STARTED: '未开课 · 待核对',
  CANCELLED: '已取消 / 过期',
}
export const summaryLabels: Record<string, string> = {
  SUBMITTED: '已提交',
  PENDING: '等待总结',
  OVERDUE: '超时未提交',
  NOT_REQUIRED: '暂不需要总结',
  UNKNOWN: '总结数据未知',
}
export const typeLabels: Record<string, string> = {
  REGULAR: '单次课程',
  FIXED: '固定课程',
  TRIAL: '试听课',
  DESIRED_FIXED: '期望固定课申请',
  DESIRED_REGULAR: '期望单次课申请',
}
export const networkLabels: Record<string, string> = {
  NORMAL: '采样正常',
  UNSTABLE: '网络波动',
  UNKNOWN: '指标不足',
}

export function attendance(
  lesson: Lesson,
  role: 'teacher' | 'student',
): { label: string; color?: string } {
  if (
    lesson.classroomStatus === 'CANCELLED' ||
    lesson.classroomStatus === 'UPCOMING'
  )
    return { label: '不适用' }
  if (
    lesson.status === 'ABSENT' &&
    (lesson.absentType === 'BOTH' || lesson.absentType === role.toUpperCase())
  )
    return { label: '缺席', color: 'error' }
  if (lesson.status === 'ABSENT' && !lesson.absentType)
    return { label: '缺席归属待核对', color: 'warning' }
  const late = role === 'teacher' ? lesson.teacherLate : lesson.studentLate
  const seconds =
    role === 'teacher' ? lesson.teacherLateSeconds : lesson.studentLateSeconds
  if (late === true)
    return {
      label: `迟到 ${Math.ceil((seconds ?? 0) / 60)} 分钟`,
      color: 'warning',
    }
  if (late === false) return { label: '已到课 · 未迟到', color: 'success' }
  return { label: '到课时间未知' }
}

export function followUp(lesson: Lesson): string {
  switch (lesson.bookingStatus) {
    case 'AWAITING_ACCEPTANCE':
      return '联系老师在截止时间前接受申请；接受前尚未预约成功。'
    case 'CREATED':
      return '提醒老师确认预约，并与学生核对上课时间。'
    case 'UNCONFIRMED':
      return '老师仍未确认，请优先联系老师，及时向学生反馈结果。'
    case 'EXPIRED':
      return '申请已过期，请与学生重新确认时间，再发起新的申请。'
    case 'CANCELLED':
      return '查看取消或拒绝原因，联系学生确认是否需要重新预约。'
    case 'CONFIRMED':
      return '预约已确认，可核对双方上课安排。'
    default:
      return '查看出勤、总结和课堂情况，跟进需要处理的问题。'
  }
}

export function selectCounter(
  key: string,
): Pick<LessonQuery, 'status' | 'focus'> {
  if (key === 'TOTAL') return { status: '', focus: '' }
  if (['ABSENT', 'LATE', 'PENDING_SUMMARY', 'FOLLOW_UP'].includes(key))
    return { status: '', focus: key }
  return { status: key, focus: '' }
}
